import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Modal,
  Progress,
  Statistic,
  Table,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import {
  importQuestions,
  importQuestionsIntoExam,
} from "../../services/questionImportService";

const { Dragger } = Upload;

const getRequestError = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ||
    data?.title ||
    data?.error ||
    (error?.response?.status === 404
      ? "آزمون مورد نظر یافت نشد"
      : "فایل اکسل پردازش نشد. ساختار فایل را بررسی کنید.")
  );
};

const ExcelQuestionImport = ({
  open,
  onClose,
  examId,
  onSuccess,
  title = "ورود سوالات از اکسل",
}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [requestError, setRequestError] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!open) {
      setFile(null);
      setUploading(false);
      setProgress(0);
      setResult(null);
      setRequestError("");
      setValidationError("");
    }
  }, [open]);

  const validateAndSetFile = (candidate) => {
    const isXlsx =
      candidate?.name?.toLowerCase().endsWith(".xlsx") ||
      candidate?.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isXlsx) {
      setFile(null);
      setValidationError("فقط فایل با پسوند .xlsx قابل قبول است.");
      return Upload.LIST_IGNORE;
    }

    setFile(candidate);
    setValidationError("");
    setRequestError("");
    setResult(null);
    setProgress(0);
    return false;
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setResult(null);
    setRequestError("");

    try {
      const importResult = examId
        ? await importQuestionsIntoExam(examId, file, setProgress)
        : await importQuestions(file, setProgress);

      setProgress(100);
      setResult(importResult);
      await onSuccess?.(importResult);
    } catch (error) {
      setRequestError(getRequestError(error));
    } finally {
      setUploading(false);
    }
  };

  const failureColumns = [
    {
      title: "ردیف اکسل",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 110,
      align: "center",
      render: (value) => value ?? "—",
    },
    {
      title: "کد سوال",
      dataIndex: "questionCode",
      key: "questionCode",
      width: 120,
      align: "center",
      render: (value) => value ?? "—",
    },
    {
      title: "دلیل خطا",
      dataIndex: "reason",
      key: "reason",
      align: "right",
    },
  ];

  const statistics = result
    ? [
        { title: "کل ردیف‌ها", value: result.totalRows },
        { title: "ایجاد شده", value: result.createdCount },
        { title: "به‌روزرسانی شده", value: result.updatedCount },
        ...(examId
          ? [
              { title: "متصل شده", value: result.attachedCount },
              { title: "از قبل متصل", value: result.alreadyAttachedCount },
            ]
          : []),
        { title: "ناموفق", value: result.failedCount, danger: true },
      ]
    : [];

  return (
    <Modal
      title={<span className="font-custom">{title}</span>}
      open={open}
      onCancel={onClose}
      width={850}
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose} disabled={uploading} className="font-custom">
          بستن
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={!file || uploading}
          loading={uploading}
          className="font-custom"
        >
          شروع پردازش
        </Button>,
      ]}
    >
      <div dir="rtl" className="font-custom">
        <Dragger
          accept=".xlsx"
          maxCount={1}
          fileList={file ? [file] : []}
          beforeUpload={validateAndSetFile}
          onRemove={() => {
            setFile(null);
            setResult(null);
            setProgress(0);
          }}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text font-custom">
            فایل اکسل را انتخاب کنید یا اینجا رها کنید
          </p>
          <p className="ant-upload-hint font-custom">فرمت مجاز: .xlsx</p>
        </Dragger>

        {(uploading || progress > 0) && (
          <Progress
            className="mt-4"
            percent={progress}
            status={requestError ? "exception" : progress === 100 ? "success" : "active"}
          />
        )}

        {validationError && (
          <Alert
            className="mt-4"
            type="warning"
            showIcon
            message="فایل نامعتبر"
            description={validationError}
          />
        )}

        {requestError && (
          <Alert
            className="mt-4"
            type="error"
            showIcon
            message="خطا در پردازش فایل"
            description={requestError}
          />
        )}

        {result && (
          <div className="mt-5">
            <Alert
              type={result.failedCount > 0 ? "warning" : "success"}
              showIcon
              message="پردازش فایل تکمیل شد"
            />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
              {statistics.map((item) => (
                <Card size="small" key={item.title}>
                  <Statistic
                    title={<span className="font-custom">{item.title}</span>}
                    value={item.value}
                    valueStyle={item.danger ? { color: "#cf1322" } : undefined}
                  />
                </Card>
              ))}
            </div>

            {result.failures.length > 0 && (
              <Collapse
                className="mt-4"
                items={[
                  {
                    key: "failures",
                    label: (
                      <span className="font-custom">
                        نمایش ردیف‌های ناموفق ({result.failures.length})
                      </span>
                    ),
                    children: (
                      <Table
                        size="small"
                        columns={failureColumns}
                        dataSource={result.failures}
                        rowKey={(record, index) =>
                          `${record.rowNumber ?? "row"}-${record.questionCode ?? index}-${index}`
                        }
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 600 }}
                      />
                    ),
                  },
                ]}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExcelQuestionImport;
